import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, FileImage, Loader2, Sparkles, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { ReceiptsApi, errorMessage } from "../lib/api";
import { formatMoney } from "../lib/format";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import StatusBadge from "./StatusBadge";
import { useI18n } from "../i18n";
import { Switch } from "./ui/switch";

const TERMINAL = ["parsed", "needs_review", "failed", "approved", "rejected"];

const SAMPLE_LABELS = {
  "receipt_fuel.jpg": "Fuel \u2014 INA station",
  "receipt_parts_tools.jpg": "Parts & tools (mixed VAT)",
  "receipt_service.jpg": "Hotel & service (13% + 25%)",
  "receipt_office.jpg": "Office supplies (VAT included)",
};

/**
 * Receipt capture panel: camera capture on mobile, drag & drop on desktop,
 * live OCR progress per file and a one-click sample receipt for first-time users.
 */
export const UploadPanel = ({ currency = "EUR", onParsed, compact = false, showSamples = true, quota = null }) => {
  const { t } = useI18n();
  const [splitPdf, setSplitPdf] = useState(false);
  const [sawPdf, setSawPdf] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [queue, setQueue] = useState([]); // {id?, name, status, receipt}
  const [uploadPct, setUploadPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const [samples, setSamples] = useState([]);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const pollRef = useRef(null);
  const mounted = useRef(true);
  const blocked = Boolean(quota?.blocked);
  // The sample-receipt helper is onboarding only: hidden for good once the workspace has
  // recorded its first scan (quota.total_scans > 0). Undefined quota -> stay hidden.
  const isNewWorkspace = Number(quota?.total_scans ?? 1) === 0;

  useEffect(() => {
    mounted.current = true;
    if (showSamples) {
      ReceiptsApi.samples()
        .then((d) => setSamples(d.samples || []))
        .catch(() => setSamples([]));
    }
    return () => {
      mounted.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [showSamples]);

  const startPolling = useCallback(
    (ids) => {
      if (pollRef.current) clearInterval(pollRef.current);
      let pending = new Set(ids);
      pollRef.current = setInterval(async () => {
        if (!mounted.current || pending.size === 0) {
          clearInterval(pollRef.current);
          return;
        }
        await Promise.all(
          Array.from(pending).map(async (id) => {
            try {
              const { receipt } = await ReceiptsApi.get(id);
              setQueue((prev) =>
                prev.map((item) =>
                  item.id === id ? { ...item, status: receipt.status, receipt } : item
                )
              );
              if (TERMINAL.includes(receipt.status)) {
                pending.delete(id);
                if (receipt.status === "failed") {
                  toast.error(t("scan.couldNotRead", { name: receipt.filename || "" }), {
                    description: receipt.warnings?.[0] || t("scan.tryBetterPhoto"),
                  });
                } else if (receipt.status === "needs_review") {
                  toast.warning(t("scan.needsReviewToast", { vendor: receipt.vendor_name || "" }), {
                    description: receipt.validation?.errors?.[0] || receipt.warnings?.[0] || "",
                  });
                } else {
                  toast.success(
                    `${receipt.vendor_name || "Receipt"} \u2014 ${formatMoney(
                      receipt.gross_total,
                      receipt.currency || currency
                    )}`,
                    {
                      description: `${t("common.vat")} ${formatMoney(
                        receipt.vat_total,
                        receipt.currency || currency
                      )}`,
                    }
                  );
                }
                onParsed && onParsed(receipt);
              }
            } catch (e) {
              pending.delete(id);
            }
          })
        );
      }, 2500);
    },
    [currency, onParsed]
  );

  const handleFiles = useCallback(
    async (fileList) => {
      const all = Array.from(fileList || []).filter((f) => f.size > 0);
      const isSupported = (f) =>
        (f.type || "").startsWith("image/") ||
        (f.type || "").toLowerCase().includes("pdf") ||
        (f.name || "").toLowerCase().endsWith(".pdf");
      const files = all.filter(isSupported);
      const rejected = all.length - files.length;
      if (files.some((f) => (f.name || "").toLowerCase().endsWith(".pdf"))) setSawPdf(true);
      if (rejected > 0) toast.error(t("scan.unsupportedFiles", { count: rejected }));
      if (!files.length) return;
      setBusy(true);
      setUploadPct(0);
      setQueue((prev) => [
        ...files.map((f) => ({ name: f.name, status: "uploading" })),
        ...prev.slice(0, 12),
      ]);
      try {
        const data = await ReceiptsApi.upload(files, setUploadPct, splitPdf);
        const created = data.receipts || [];
        setQueue((prev) => {
          const rest = prev.filter((i) => i.status !== "uploading");
          return [
            ...created.map((r) => ({ id: r.id, name: r.filename, status: r.status, receipt: r })),
            ...rest,
          ].slice(0, 15);
        });
        (data.errors || []).forEach((e) =>
          toast.error(`${e.filename}: ${e.error}`)
        );
        if (created.length) {
          toast.info(
            created.length === 1 ? "Scanning receipt\u2026" : `Scanning ${created.length} receipts\u2026`
          );
          startPolling(created.map((r) => r.id));
        }
      } catch (e) {
        setQueue((prev) => prev.filter((i) => i.status !== "uploading"));
        toast.error(errorMessage(e, "Upload failed"));
      } finally {
        setBusy(false);
        setUploadPct(0);
        if (fileRef.current) fileRef.current.value = "";
        if (cameraRef.current) cameraRef.current.value = "";
      }
    },
    [startPolling]
  );

  const handleSample = useCallback(
    async (name) => {
      setBusy(true);
      try {
        const data = await ReceiptsApi.sample(name);
        const created = data.receipts || [];
        setQueue((prev) => [
          ...created.map((r) => ({ id: r.id, name: SAMPLE_LABELS[name] || r.filename, status: r.status, receipt: r })),
          ...prev,
        ].slice(0, 15));
        toast.info(t("scan.scanningSample"));
        startPolling(created.map((r) => r.id));
      } catch (e) {
        toast.error(errorMessage(e, "Could not scan the example"));
      } finally {
        setBusy(false);
      }
    },
    [startPolling]
  );

  return (
    <div className="space-y-4">
      <div
        data-testid="receipt-upload-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          "rounded-xl border border-dashed bg-card p-5 text-center transition-colors md:p-8",
          dragging ? "border-primary bg-primary/5" : "border-border"
        )}
      >
        <div className="mx-auto flex max-w-md flex-col items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          </span>
          <div>
            <p className="font-display text-base font-semibold">{t("scan.dropTitle")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t("scan.dropSub")}</p>
            {sawPdf ? (
              <label
                className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg border bg-card px-3 py-2 text-left"
                onClick={(e) => e.stopPropagation()}
                data-testid="pdf-split-toggle"
              >
                <Switch checked={splitPdf} onCheckedChange={setSplitPdf} />
                <span className="text-xs">
                  <span className="block font-medium">{t("scan.splitPdf")}</span>
                  <span className="block text-muted-foreground">{t("scan.splitPdfHint")}</span>
                </span>
              </label>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              type="button"
              size="lg"
              onClick={() => cameraRef.current?.click()}
              disabled={busy || blocked}
              data-testid="receipt-upload-camera-button"
            >
              <Camera className="mr-2 h-4 w-4" /> {t("scan.takePhoto")}
            </Button>
            <Button
              type="button"
              size="lg"
              variant="secondary"
              onClick={() => fileRef.current?.click()}
              disabled={busy || blocked}
              data-testid="receipt-upload-browse-button"
            >
              <FileImage className="mr-2 h-4 w-4" /> {t("scan.chooseFiles")}
            </Button>
          </div>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*,application/pdf"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="receipt-upload-camera-input"
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="receipt-upload-file-input"
          />
          {busy && uploadPct > 0 ? (
            <Progress value={uploadPct} className="mt-2 h-1.5 w-full" />
          ) : null}
        </div>
      </div>

      {showSamples && isNewWorkspace && samples.length ? (
        <div className="ll-card p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Sparkles className="h-4 w-4 text-primary" /> {t("scan.sampleTitle")}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {samples.map((s) => (
              <Button
                key={s}
                type="button"
                size="sm"
                variant="outline"
                disabled={busy || blocked}
                onClick={() => handleSample(s)}
                data-testid={`receipt-sample-${s.replace(/\W+/g, "-")}`}
              >
                {SAMPLE_LABELS[s] || s}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {queue.length ? (
        <div className="ll-card divide-y" data-testid="upload-queue">
          {queue.map((item, idx) => (
            <div
              key={`${item.id || item.name}-${idx}`}
              className="flex items-center justify-between gap-3 p-3"
              data-testid="upload-queue-item"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <FileImage className="h-4 w-4 text-muted-foreground" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{item.name || "receipt"}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {item.receipt?.vendor_name
                      ? `${item.receipt.vendor_name}${
                          item.receipt.receipt_number ? ` \u00b7 no. ${item.receipt.receipt_number}` : ""
                        }`
                      : item.status === "uploading"
                        ? t("status.uploading")
                        : t("scan.readingOcr")}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {item.receipt?.gross_total != null ? (
                  <div className="text-right">
                    <div className="ll-num text-sm font-semibold">
                      {formatMoney(item.receipt.gross_total, item.receipt.currency || currency)}
                    </div>
                    <div className="ll-num text-xs text-muted-foreground">
                      {t("common.vat")} {formatMoney(item.receipt.vat_total, item.receipt.currency || currency)}
                    </div>
                  </div>
                ) : null}
                {item.status === "uploading" ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("status.uploading")}
                  </span>
                ) : (
                  <StatusBadge status={item.status} />
                )}
                {!compact ? (
                  <button
                    type="button"
                    aria-label="Dismiss"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => setQueue((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default UploadPanel;
